<template>
  <div class="container mt-5">
    <div class="card modern-card">
      <div class="card-header">
        <h4 class="mb-0">
          Carte bancaire
          Rechargement via Stripe
        </h4>
      </div>

      <div class="card-body">
        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-primary"></div>
          <p class="mt-3 text-muted">Préparation du paiement...</p>
        </div>

        <div v-else-if="error" class="alert alert-danger">
          {{ error }}
        </div>

        <div v-else>
          <div class="payment-info mb-4">
            <h5 class="info-title">Détails du rechargement</h5>
            <div class="info-content">
              <div class="info-row">
                <span class="info-label">Compte prépayé</span>
                <span class="info-value">{{ accountNumber }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Solde actuel</span>
                <span class="info-value">{{ formatNumber(currentBalance) }} {{ currency }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Montant à recharger</span>
                <span class="info-value text-success fw-bold">{{ formatNumber(amount) }} {{ currency }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Nouveau solde estimé</span>
                <span class="info-value text-primary fw-bold">{{ formatNumber(currentBalance + amount) }} {{ currency }}</span>
              </div>
            </div>
          </div>

          <div class="alert alert-info-custom mb-4">
            Paiement 100% sécurisé par Stripe · SSL · 3D Secure
          </div>

          <!-- Stripe Elements -->
          <div id="payment-element" class="mb-4"></div>

          <div v-if="processing" class="text-center my-4">
            <div class="spinner-border text-primary"></div>
            <p class="mt-2">Traitement du paiement...</p>
          </div>

          <div class="d-flex gap-3 mt-4">
            <button
              @click="payWithStripe"
              :disabled="processing"
              class="btn btn-success flex-fill py-3 fs-5">
              <span v-if="!processing">Payer {{ formatNumber(amount) }} {{ currency }}</span>
              <span v-else><span class="spinner-border spinner-border-sm me-2"></span>Traitement...</span>
            </button>
            <router-link to="/prepaid-accounts" class="btn btn-outline-secondary">
              Annuler
            </router-link>
          </div>

          <div class="text-center mt-4">
            <small class="secure-badge">
              Paiement sécurisé par Stripe
            </small>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import ApiService from '@/services/ApiService';
import { error as showError, success as showSuccess } from '@/utils/utils';
import { loadStripe } from '@stripe/stripe-js';

export default defineComponent({
  name: 'RechargeStripe',
  setup() {
    const route = useRoute();
    const router = useRouter();

    const intentId = ref(route.query.intentId as string);
    const amount = ref(0);
    const currency = ref('EUROS');
    const accountNumber = ref('');
    const currentBalance = ref(0);
    const loading = ref(true);
    const error = ref('');
    const processing = ref(false);
    let stripe: any = null;
    let elements: any = null;

    const formatNumber = (val: number) => new Intl.NumberFormat('fr-FR').format(val);

    const loadRechargeInfo = async () => {
      try {
        const { data } = await ApiService.get(`/prepaid/recharge/intent/${intentId.value}`);
        accountNumber.value = data.data.accountNumber;
        currentBalance.value = data.data.currentBalance;
        amount.value = data.data.amount;
        currency.value = data.data.currency;
        loading.value = false;
      } catch (err: any) {
        error.value = 'Impossible de charger les informations';
        loading.value = false;
      }
    };

   const initStripe = async () => {
  try {
    console.log('Initialisation Stripe pour rechargement prépayé...');

    // RÉCUPÉRATION SÉCURISÉE DE LA CLÉ (Vue CLI)
    const rawKey = process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY;
    const publishableKey = rawKey?.trim() || 
      'pk_test_51JDXTeGXfBDkYkwpKxt2AjIL2ZXSXWGGkqXJpjNKwGTW0AjZJIONLDUYVa3TyY1QSveflJWBkc4n0Suu05bE9TJM00aeMQAQlI';

    console.log('Clé Stripe trouvée dans .env ?', !!rawKey);
    console.log('Clé utilisée (début) :', publishableKey.substring(0, 20) + '...');

    // VÉRIFICATIONS DE SÉCURITÉ
    if (!publishableKey) {
      error.value = 'Clé Stripe manquante (VUE_APP_STRIPE_PUBLISHABLE_KEY)';
      console.error('VUE_APP_STRIPE_PUBLISHABLE_KEY non défini dans .env');
      return;
    }

    if (!publishableKey.startsWith('pk_test_') && !publishableKey.startsWith('pk_live_')) {
      error.value = 'Clé Stripe invalide (doit commencer par pk_test_ ou pk_live_)';
      console.error('Clé invalide détectée :', publishableKey);
      return;
    }

    console.log('Chargement de Stripe...');
    const stripePromise = loadStripe(publishableKey);
    stripe = await stripePromise;

    if (!stripe) {
      throw new Error('Échec du chargement de Stripe.js');
    }

    console.log('Stripe chargé avec succès');

    // CRÉATION DU PAYMENT INTENT
    console.log('Création du PaymentIntent...');
    const response = await ApiService.post('/prepaid/recharge/stripe/create-intent', {
      intentId: intentId.value
    });

    console.log('Réponse backend :', response.data);

    let clientSecret: string | undefined;

    if (response.data?.data?.clientSecret) {
      clientSecret = response.data.data.clientSecret;
    } else if (response.data?.clientSecret) {
      clientSecret = response.data.clientSecret;
    }

    if (!clientSecret) {
      console.error('Structure de réponse :', JSON.stringify(response.data, null, 2));
      throw new Error('clientSecret manquant dans la réponse serveur');
    }

    console.log('ClientSecret reçu :', clientSecret.substring(0, 30) + '...');

    // CRÉATION DES ÉLÉMENTS
    elements = stripe.elements({ clientSecret });

    const paymentElement = elements.create('payment', {
      layout: 'tabs',
      fields: {
        billingDetails: {
          address: { country: 'auto' }
        }
      }
    });

    // MONTAGE + ATTENTE DU "ready"
    await new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Timeout : Payment Element non monté'));
      }, 10000);

      paymentElement.on('ready', () => {
        clearTimeout(timeout);
        console.log('Payment Element prêt et monté !');
        resolve();
      });

      paymentElement.on('loaderror', (e: any) => {
        clearTimeout(timeout);
        console.error('Erreur chargement Payment Element :', e);
        reject(new Error('Erreur de chargement Stripe'));
      });

      console.log('Montage du Payment Element...');
      paymentElement.mount('#payment-element');
    });

    processing.value = false;

  } catch (err: any) {
    console.error('Erreur initStripe :', err);
    error.value = err.message || 'Erreur lors de l’initialisation de Stripe';
    processing.value = false;
  }
};
    const payWithStripe = async () => {
  if (processing.value || !stripe || !elements) return;
  processing.value = true;

  try {
    const { error: stripeError } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/PrepaidRechargeSuccess?intentId=${intentId.value}&provider=stripe`,
      },
    });

    if (stripeError) {
      showError(stripeError.message || 'Paiement refusé');
      processing.value = false;
    }
    // Si pas d'erreur, Stripe redirige automatiquement
  } catch (err) {
    showError('Erreur lors du paiement');
    processing.value = false;
  }
};

    onMounted(async () => {
      if (!intentId.value) {
        error.value = 'Lien invalide';
        loading.value = false;
        return;
      }
      await loadRechargeInfo();
      if (!error.value) await initStripe();
    });

    return {
      accountNumber,
      currentBalance,
      amount,
      currency,
      loading,
      error,
      processing,
      formatNumber,
      payWithStripe
    };
  }
});
</script>

<style scoped>
.modern-card {
  border: none;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.card-header {
  background: linear-gradient(135deg, #0070ba 0%, #1546a0 100%);
  color: white !important;
  padding: 1.5rem;
  border: none;
}

.card-header h4 {
  font-weight: 600;
  font-size: 1.25rem;
  color: white !important;
}

.card-body {
  padding: 2rem;
}

.payment-info {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  border-left: 4px solid #0070ba;
}

.info-title {
  font-size: 1rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 1rem;
}

.info-content {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.info-row {
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-bottom: 1px solid #e9ecef;
}

.info-row:last-child {
  border-bottom: none;
  padding-top: 1rem;
  margin-top: 0.5rem;
  border-top: 2px solid #dee2e6;
}

.info-label {
  color: #6c757d;
  font-size: 0.95rem;
}

.info-value {
  color: #2c3e50;
  font-weight: 600;
  font-size: 0.95rem;
}

.alert-info-custom {
  background-color: #e7f3ff;
  border: 1px solid #b3d9ff;
  border-radius: 8px;
  padding: 1rem;
  color: #0070ba;
}

#paypal-button-container {
  min-height: 150px;
  max-width: 500px;
  margin: 0 auto;
}

.btn-cancel {
  border-radius: 8px;
  padding: 0.625rem 1.5rem;
  font-weight: 500;
  transition: all 0.3s ease;
}

.btn-cancel:hover {
  background-color: #f8f9fa;
  border-color: #adb5bd;
  transform: translateY(-1px);
}

.secure-badge {
  display: inline-flex;
  align-items: center;
  color: #6c757d;
  font-size: 0.875rem;
}

.secure-badge i {
  color: #28a745;
}

.spinner-border {
  width: 2rem;
  height: 2rem;
  border-width: 0.25em;
}

@media (max-width: 576px) {
  .card-body {
    padding: 1.5rem;
  }
  
  .payment-info {
    padding: 1rem;
  }
}
</style>